import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import { getProgressReport } from '../../services/apiService';

const LEVEL_COLOR = { critical: 'error', warning: 'warning', good: 'success', none: 'default' };
const LEVEL_LABEL = { critical: 'Critical', warning: 'Warning', good: 'Good', none: '—' };
const RESOURCE_LABEL = { memory: 'Memory', threads: 'Threads', db: 'DB Connections' };

const OUTCOME_META = {
  resolved:   { label: 'Resolved',   color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  improving:  { label: 'Improving',  color: 'info',    icon: <TrendingUpIcon fontSize="small" /> },
  no_change:  { label: 'No change',  color: 'default', icon: <RemoveIcon fontSize="small" /> },
  worsened:   { label: 'Worsened',   color: 'error',   icon: <WarningAmberIcon fontSize="small" /> },
  no_data:    { label: 'No data',    color: 'default', icon: <HelpOutlineIcon fontSize="small" /> }
};

function SummaryCard({ label, value, color, subtitle }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, minWidth: 130, flex: '1 1 130px', borderRadius: 2, borderColor: `${color}.main`, borderWidth: 2 }}
    >
      <Typography variant="h4" fontWeight={700} color={`${color}.main`} lineHeight={1}>
        {value}
      </Typography>
      <Typography variant="body2" fontWeight={600} mt={0.5}>
        {label}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
}

function LevelChip({ level }) {
  return (
    <Chip
      label={LEVEL_LABEL[level] ?? '—'}
      color={LEVEL_COLOR[level] ?? 'default'}
      size="small"
      variant={level === 'none' || !level ? 'outlined' : 'filled'}
      sx={{ fontWeight: 600, minWidth: 72 }}
    />
  );
}

function ViolCount({ value }) {
  if (value == null) return <Typography variant="body2" color="text.disabled">—</Typography>;
  return <Typography variant="body2">{value.toLocaleString()}/day avg</Typography>;
}

function ViolDelta({ before, after }) {
  if (before == null || after == null) return null;
  const diff = after - before;
  if (diff === 0) return null;
  const pct = before === 0 ? null : Math.round(Math.abs(diff / before) * 100);
  const improved = diff < 0;
  return (
    <Typography variant="caption" color={improved ? 'success.main' : 'error.main'} display="block" fontWeight={600}>
      {improved ? '▼' : '▲'} {pct != null ? `${pct}%` : Math.abs(diff)}
    </Typography>
  );
}

function ResourceValue({ resourceType, value, unit }) {
  if (value == null) return '—';
  if (resourceType === 'memory') {
    const gib = (value / 1024).toFixed(1);
    return `${gib} GiB`;
  }
  return String(value);
}

function DailyActivityChart({ dailyActivity }) {
  if (!dailyActivity || dailyActivity.length === 0) return null;
  const maxVal = Math.max(...dailyActivity.map((d) => d.upgrades + d.downgrades), 1);

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
        Daily upgrade activity
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 80, overflowX: 'auto' }}>
        {dailyActivity.map((day) => {
          const total = day.upgrades + day.downgrades;
          const heightPct = Math.round((total / maxVal) * 100);
          const resolvedPct = total > 0 ? Math.round((day.resolved / total) * 100) : 0;
          return (
            <Tooltip
              key={day.date}
              title={`${day.date}: ${day.upgrades} upgrade(s), ${day.downgrades} downgrade(s), ${day.resolved} resolved`}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  minWidth: 20,
                  flex: 1,
                  height: '100%',
                  cursor: 'default'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: `${heightPct}%`,
                    bgcolor: resolvedPct > 50 ? 'success.light' : 'primary.light',
                    borderRadius: '2px 2px 0 0',
                    minHeight: total > 0 ? 4 : 0
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {dailyActivity[0]?.date}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {dailyActivity[dailyActivity.length - 1]?.date}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: 'success.light', borderRadius: 0.5 }} />
          <Typography variant="caption">Majority resolved</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: 'primary.light', borderRadius: 0.5 }} />
          <Typography variant="caption">Upgrades / changes</Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default function ProductionProgressTab() {
  const [days, setDays] = useState(90);
  const [clientSearch, setClientSearch] = useState('');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (selectedDays) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getProgressReport(selectedDays);
      if (res?.success) {
        setData(res.data);
      } else {
        setError(res?.message || 'Failed to load progress report');
      }
    } catch (err) {
      setError(err.message || 'Failed to load progress report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [load, days]);

  const summary = data?.summary;
  const allEvents = data?.events ?? [];

  const regions = [...new Set(allEvents.map((e) => e.region).filter(Boolean))].sort();

  const clientQuery = clientSearch.trim().toLowerCase();
  const filteredEvents = allEvents.filter((e) => {
    if (clientQuery) {
      const name = ((e.friendlyName || e.instanceName) || '').toLowerCase();
      if (!name.includes(clientQuery)) return false;
    }
    if (resourceFilter !== 'all' && e.resourceType !== resourceFilter) return false;
    if (regionFilter !== 'all' && e.region !== regionFilter) return false;
    if (outcomeFilter === 'upgrades' && !e.isUpgrade) return false;
    if (outcomeFilter === 'downgrades' && e.isUpgrade) return false;
    if (outcomeFilter !== 'all' && outcomeFilter !== 'upgrades' && outcomeFilter !== 'downgrades' && e.outcome !== outcomeFilter) return false;
    return true;
  });

  return (
    <Box sx={{ p: 0 }}>
      {/* Summary cards */}
      {summary && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <SummaryCard label="Total upgrades" value={summary.upgrades} color="primary" subtitle={`${summary.downgrades} downgrade(s)`} />
          <SummaryCard label="Resolved" value={summary.resolved} color="success" subtitle="issue cleared after upgrade" />
          <SummaryCard label="Improving" value={summary.improving} color="info" subtitle="level improved, not yet good" />
          <SummaryCard label="No change" value={summary.noChange} color="warning" subtitle="level unchanged" />
          <SummaryCard label="Worsened" value={summary.worsened} color="error" subtitle="level got worse after upgrade" />
        </Box>
      )}

      {summary && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`Memory: ${summary.byResourceType.memory}`} size="small" variant="outlined" />
          <Chip label={`Threads: ${summary.byResourceType.threads}`} size="small" variant="outlined" />
          <Chip label={`DB Connections: ${summary.byResourceType.db}`} size="small" variant="outlined" />
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search client…"
          value={clientSearch}
          onChange={(e) => setClientSearch(e.target.value)}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
        />

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Period</InputLabel>
          <Select value={days} label="Period" onChange={(e) => setDays(e.target.value)}>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={60}>Last 60 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
            <MenuItem value={180}>Last 180 days</MenuItem>
            <MenuItem value={365}>Last 365 days</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Resource</InputLabel>
          <Select value={resourceFilter} label="Resource" onChange={(e) => setResourceFilter(e.target.value)}>
            <MenuItem value="all">All resources</MenuItem>
            <MenuItem value="memory">Memory</MenuItem>
            <MenuItem value="threads">Threads</MenuItem>
            <MenuItem value="db">DB Connections</MenuItem>
          </Select>
        </FormControl>

        {regions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Region</InputLabel>
            <Select value={regionFilter} label="Region" onChange={(e) => setRegionFilter(e.target.value)}>
              <MenuItem value="all">All regions</MenuItem>
              {regions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Outcome</InputLabel>
          <Select value={outcomeFilter} label="Outcome" onChange={(e) => setOutcomeFilter(e.target.value)}>
            <MenuItem value="all">All outcomes</MenuItem>
            <MenuItem value="upgrades">Upgrades only</MenuItem>
            <MenuItem value="downgrades">Downgrades only</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="improving">Improving</MenuItem>
            <MenuItem value="no_change">No change</MenuItem>
            <MenuItem value="worsened">Worsened</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
          <CircularProgress size={22} />
          <Typography color="text.secondary">Loading progress report…</Typography>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && data && (
        <>
          <DailyActivityChart dailyActivity={data.dailyActivity} />

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Region</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Resource</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Change</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Level before</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Level after</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Violations before</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Violations after</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Outcome</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEvents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {clientQuery
                        ? `No events found for client "${clientSearch}".`
                        : 'No upgrade events found for the selected filters.'}
                    </TableCell>
                  </TableRow>
                )}
                {filteredEvents.map((event, idx) => {
                  const meta = OUTCOME_META[event.outcome] ?? OUTCOME_META.no_data;
                  return (
                    <TableRow
                      key={`${event.stableKey}-${event.resourceType}-${event.changeDate}-${idx}`}
                      sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <TableCell
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setClientSearch(event.friendlyName || event.instanceName)}
                      >
                        <Tooltip title="Click to filter by this client">
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 180, '&:hover': { color: 'primary.main' } }}>
                            {event.friendlyName || event.instanceName}
                          </Typography>
                        </Tooltip>
                        {event.friendlyName && event.instanceName !== event.friendlyName && (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {event.instanceName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{event.region || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{event.changeDate}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={RESOURCE_LABEL[event.resourceType]}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {event.isUpgrade
                            ? <ArrowUpwardIcon fontSize="small" color="success" />
                            : <ArrowDownwardIcon fontSize="small" color="error" />}
                          <Typography variant="body2">
                            <ResourceValue resourceType={event.resourceType} value={event.valueBefore} /> → <ResourceValue resourceType={event.resourceType} value={event.valueAfter} />
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell><LevelChip level={event.levelBefore} /></TableCell>
                      <TableCell><LevelChip level={event.levelAfter} /></TableCell>
                      <TableCell>
                        <ViolCount value={event.violationsBefore} />
                      </TableCell>
                      <TableCell>
                        <ViolCount value={event.violationsAfter} />
                        <ViolDelta before={event.violationsBefore} after={event.violationsAfter} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={meta.icon}
                          label={meta.label}
                          color={meta.color}
                          size="small"
                          variant={event.outcome === 'no_data' ? 'outlined' : 'filled'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredEvents.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {filteredEvents.length} event(s) shown. Violations = 7-day daily average before/after the change date. Level = nearest recommendation snapshot.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
